import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Heart, CheckSquare, Users, BookOpen, UserPlus, Library, LogOut, Menu, X, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState({
    tasks: 0,
    visits: 0,
    studies: 0,
    prospects: 0
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    fetchUser();
    fetchStats();
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      console.error("Failed to fetch user", error);
    }
  };

  const fetchStats = async () => {
    try {
      const [tasks, visits, studies, prospects] = await Promise.all([
        axios.get(`${API}/tasks`, { withCredentials: true }),
        axios.get(`${API}/visits`, { withCredentials: true }),
        axios.get(`${API}/bible-studies`, { withCredentials: true }),
        axios.get(`${API}/prospects`, { withCredentials: true })
      ]);

      setStats({
        tasks: tasks.data.length,
        visits: visits.data.length,
        studies: studies.data.length,
        prospects: prospects.data.length
      });
    } catch (error) {
      console.error("Failed to fetch stats", error);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      // Clear localStorage and axios headers for Safari compatibility
      localStorage.removeItem('session_token');
      delete axios.defaults.headers.common['Authorization'];
      toast.success("Logged out successfully");
      navigate("/");
    } catch (error) {
      console.error("Logout failed", error);
      toast.error("Logout failed");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-amber-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Heart className="w-7 h-7 text-emerald-700" />
              <span className="text-xl font-bold text-gray-800">Soul Aid</span>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-6">
              <NavLink icon={<CheckSquare className="w-4 h-4" />} to="/tasks">Tasks</NavLink>
              <NavLink icon={<Users className="w-4 h-4" />} to="/visits">Visits</NavLink>
              <NavLink icon={<BookOpen className="w-4 h-4" />} to="/bible-studies">Bible Studies</NavLink>
              <NavLink icon={<UserPlus className="w-4 h-4" />} to="/prospects">Prospects</NavLink>
              <NavLink icon={<Library className="w-4 h-4" />} to="/custom-studies">Custom Studies</NavLink>
              <NavLink icon={<MessageSquare className="w-4 h-4" />} to="/messages">Messages</NavLink>
            </nav>

            <div className="flex items-center space-x-4">
              {user && (
                <div className="hidden md:flex items-center space-x-3">
                  <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                  <span className="text-sm text-gray-700">{user.name}</span>
                </div>
              )}
              <Button 
                data-testid="logout-button"
                onClick={handleLogout} 
                variant="ghost" 
                size="sm"
                className="hidden md:flex"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
              <Button
                data-testid="mobile-menu-toggle"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                variant="ghost"
                size="sm"
                className="md:hidden"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <nav className="md:hidden mt-4 pb-4 space-y-2">
              <MobileNavLink icon={<CheckSquare className="w-4 h-4" />} to="/tasks" onClick={() => setMobileMenuOpen(false)}>Tasks</MobileNavLink>
              <MobileNavLink icon={<Users className="w-4 h-4" />} to="/visits" onClick={() => setMobileMenuOpen(false)}>Visits</MobileNavLink>
              <MobileNavLink icon={<BookOpen className="w-4 h-4" />} to="/bible-studies" onClick={() => setMobileMenuOpen(false)}>Bible Studies</MobileNavLink>
              <MobileNavLink icon={<UserPlus className="w-4 h-4" />} to="/prospects" onClick={() => setMobileMenuOpen(false)}>Prospects</MobileNavLink>
              <MobileNavLink icon={<Library className="w-4 h-4" />} to="/custom-studies" onClick={() => setMobileMenuOpen(false)}>Custom Studies</MobileNavLink>
              <MobileNavLink icon={<MessageSquare className="w-4 h-4" />} to="/messages" onClick={() => setMobileMenuOpen(false)}>Messages</MobileNavLink>
              <Button onClick={handleLogout} variant="ghost" size="sm" className="w-full justify-start">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </nav>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-12">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center space-x-4 mb-4">
            <img src="/favicon.svg" alt="Soul Aid Icon" className="w-12 h-12 sm:w-16 sm:h-16" />
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900">Welcome back!</h1>
          </div>
          <p className="text-lg text-gray-600 mb-12">Continue your ministry work and track your evangelism activities.</p>

          {/* Stats Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <StatCard 
              icon={<CheckSquare className="w-8 h-8 text-blue-600" />}
              title="Tasks"
              value={stats.tasks}
              onClick={() => navigate("/tasks")}
            />
            <StatCard 
              icon={<Users className="w-8 h-8 text-green-600" />}
              title="Personal Visits"
              value={stats.visits}
              onClick={() => navigate("/visits")}
            />
            <StatCard 
              icon={<BookOpen className="w-8 h-8 text-purple-600" />}
              title="Bible Studies"
              value={stats.studies}
              onClick={() => navigate("/bible-studies")}
            />
            <StatCard 
              icon={<UserPlus className="w-8 h-8 text-orange-600" />}
              title="Prospects"
              value={stats.prospects}
              onClick={() => navigate("/prospects")}
            />
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-3xl shadow-lg p-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">Quick Actions</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <ActionButton 
                icon={<CheckSquare className="w-5 h-5" />}
                label="Add Task"
                onClick={() => navigate("/tasks")}
              />
              <ActionButton 
                icon={<Users className="w-5 h-5" />}
                label="Record Visit"
                onClick={() => navigate("/visits")}
              />
              <ActionButton 
                icon={<BookOpen className="w-5 h-5" />}
                label="Log Bible Study"
                onClick={() => navigate("/bible-studies")}
              />
              <ActionButton 
                icon={<UserPlus className="w-5 h-5" />}
                label="Add Prospect"
                onClick={() => navigate("/prospects")}
              />
              <ActionButton 
                icon={<Library className="w-5 h-5" />}
                label="Create Study"
                onClick={() => navigate("/custom-studies")}
              />
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 mt-12">
        <div className="container mx-auto px-4">
          <p className="text-center text-sm text-gray-600">
            © 2024 Abject Ministries. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

function NavLink({ icon, to, children }) {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate(to)}
      className="flex items-center space-x-2 text-gray-700 hover:text-emerald-700 transition-colors duration-200 font-medium"
    >
      {icon}
      <span>{children}</span>
    </button>
  );
}

function MobileNavLink({ icon, to, children, onClick }) {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => {
        navigate(to);
        onClick();
      }}
      className="flex items-center space-x-2 text-gray-700 hover:text-emerald-700 transition-colors duration-200 font-medium w-full py-2"
    >
      {icon}
      <span>{children}</span>
    </button>
  );
}

function StatCard({ icon, title, value, onClick }) {
  return (
    <div 
      onClick={onClick}
      className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer group"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="p-3 bg-gray-50 rounded-xl group-hover:bg-gray-100 transition-colors">
          {icon}
        </div>
      </div>
      <h3 className="text-3xl font-bold text-gray-900 mb-1">{value}</h3>
      <p className="text-sm text-gray-600">{title}</p>
    </div>
  );
}

function ActionButton({ icon, label, onClick }) {
  return (
    <Button
      data-testid={`action-${label.toLowerCase().replace(/\s+/g, '-')}`}
      onClick={onClick}
      variant="outline"
      className="flex items-center space-x-2 justify-start h-auto py-4 hover:bg-emerald-50 hover:border-emerald-300 transition-colors duration-200"
    >
      {icon}
      <span>{label}</span>
    </Button>
  );
}
